import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { useDocumentTitle } from '../hooks/use-document-title';
import { ContactDetails } from '../components/ContactDetails';

export default function PrivacyPolicy() {
  useDocumentTitle('Privacy Policy');
  const lastUpdated = "October 14, 2026";

  return (
    <div className="min-h-screen flex flex-col bg-paper">
      <Navbar />
      <main id="main-content" className="flex-1 w-full max-w-4xl mx-auto px-4 sm:px-6 pt-28 pb-16">
        <article className="bg-white rounded-lg shadow-card border border-border p-6 sm:p-8 md:p-12">
          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold text-ink mb-4">Privacy Policy</h1>
          <p className="text-gray-600 mb-8">Last Updated: {lastUpdated}</p>

          <div className="max-w-none space-y-8">
            <section>
              <h2 className="font-serif text-xl sm:text-2xl font-semibold text-ink mb-4">1. Information We Collect</h2>
              <p className="text-gray-700 leading-relaxed">
                We collect information you provide directly to us when you create an account, place an order, 
                or communicate with us. This may include your name, email address, phone number, shipping address, 
                and order details. We also collect information automatically when you use our service, such as 
                your IP address, browser type, and usage patterns.
              </p>
              <p className="text-gray-700 leading-relaxed mt-3">
                Calls to our phone line may be recorded, and text messages with our SMS service are saved. We use
                these recordings and messages to answer support requests and to improve our phone and SMS service,
                including our speech recognition. They are kept privately and are not sold.
              </p>
            </section>

            <section>
              <h2 className="font-serif text-xl sm:text-2xl font-semibold text-ink mb-4">2. How We Use Your Information</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                We use the information we collect to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Process and fulfill your custom invitation orders</li>
                <li>Communicate with you about your order status and account</li>
                <li>Send you order confirmations and design proofs</li>
                <li>Provide customer support and respond to your inquiries</li>
                <li>Improve our services and user experience</li>
                <li>Send you transactional messages related to your orders</li>
                <li>Comply with legal obligations and protect our rights</li>
              </ul>
            </section>

            <section>
              <h2 className="font-serif text-xl sm:text-2xl font-semibold text-ink mb-4">3. Information Sharing</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                We are committed to protecting your privacy. We do not share, sell, or rent your personal 
                information to third parties for marketing purposes.
              </p>
              <p className="text-gray-700 leading-relaxed mb-3">
                We may share your information only in the following circumstances:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>With service providers who assist us in operating our business (such as payment processors, 
                    shipping companies, and hosting providers) under strict confidentiality agreements</li>
                <li>When required by law or to respond to legal process</li>
                <li>To protect our rights, property, or safety, or that of our users</li>
                <li>In connection with a business transfer or merger, with notice to you</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                <strong>Important:</strong> No mobile information will be shared with third parties or affiliates 
                for marketing or promotional purposes. All the above categories exclude text messaging originator 
                opt-in data and consent; this information will not be shared with any third parties.
              </p>
            </section>

            <section>
              <h2 className="font-serif text-xl sm:text-2xl font-semibold text-ink mb-4">4. Data Security</h2>
              <p className="text-gray-700 leading-relaxed">
                We take reasonable measures to help protect information about you from loss, theft, misuse, 
                and unauthorized access, disclosure, alteration, and destruction. We use industry-standard 
                security measures, including encryption, secure servers, and access controls. However, no method 
                of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee 
                absolute security.
              </p>
            </section>

            <section>
              <h2 className="font-serif text-xl sm:text-2xl font-semibold text-ink mb-4">5. Your Rights and Choices</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                You have certain rights regarding your personal information:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Access and review your personal information</li>
                <li>Request correction of inaccurate information</li>
                <li>Request deletion of your personal information</li>
                <li>Opt-out of marketing communications</li>
                <li>Cancel SMS services by texting "STOP" to our service number</li>
              </ul>
            </section>

            <section>
              <h2 className="font-serif text-xl sm:text-2xl font-semibold text-ink mb-4">6. Cookies and Tracking Technologies</h2>
              <p className="text-gray-700 leading-relaxed">
                We use cookies and similar tracking technologies to collect and use information about you. 
                Cookies are small data files stored on your device that help us improve your experience, 
                analyze site usage, and assist in our marketing efforts. You can control cookies through 
                your browser settings, but disabling cookies may limit your ability to use certain features 
                of our service.
              </p>
            </section>

            <section>
              <h2 className="font-serif text-xl sm:text-2xl font-semibold text-ink mb-4">7. Children's Privacy</h2>
              <p className="text-gray-700 leading-relaxed">
                Our service is not intended for children under the age of 13. We do not knowingly collect 
                personal information from children under 13. If you believe we have collected information 
                from a child under 13, please contact us immediately so we can delete such information.
              </p>
            </section>

            <section>
              <h2 className="font-serif text-xl sm:text-2xl font-semibold text-ink mb-4">8. Changes to This Privacy Policy</h2>
              <p className="text-gray-700 leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any changes by 
                posting the new Privacy Policy on this page and updating the "Last Updated" date. You are 
                advised to review this Privacy Policy periodically for any changes.
              </p>
            </section>

            <section>
              <h2 className="font-serif text-xl sm:text-2xl font-semibold text-ink mb-4">9. Contact Us</h2>
              <ContactDetails topic="this Privacy Policy or our privacy practices" />
            </section>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}
